import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { IoTimeOutline, IoTrendingUpOutline, IoTrendingDownOutline } from 'react-icons/io5';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface MetricHistoryEntry {
  id: string;
  metric_value: number;
  metric_date: string;
  notes?: string;
}

interface MetricsHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  metricName: string;
  history: MetricHistoryEntry[];
  unit?: string;
  target?: number;
}

const MetricsHistory: React.FC<MetricsHistoryProps> = ({
  isOpen,
  onClose,
  metricName,
  history,
  unit,
  target
}) => {
  const { darkMode } = useDarkMode();

  // Sort history by date
  const sortedHistory = [...history].sort((a, b) => 
    new Date(a.metric_date).getTime() - new Date(b.metric_date).getTime()
  );

  // Prepare chart data for recharts
  const chartData = sortedHistory.map(h => ({
    date: new Date(h.metric_date).toLocaleDateString(),
    value: h.metric_value,
    target: target || null
  }));

  // Calculate trend
  const calculateTrend = () => {
    if (sortedHistory.length < 2) return null;
    const firstValue = sortedHistory[0].metric_value;
    const lastValue = sortedHistory[sortedHistory.length - 1].metric_value;
    const change = ((lastValue - firstValue) / firstValue) * 100;
    return {
      value: change,
      direction: change > 0 ? 'up' : 'down'
    };
  };

  const trend = calculateTrend();

  // Style classes
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50" />

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`relative w-full max-w-4xl max-h-[90vh] ${bgColor} rounded-xl shadow-2xl overflow-hidden`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
            <div>
              <h2 className={`text-xl font-semibold ${textColor}`}>
                {metricName} History
              </h2>
              {trend && (
                <div className="flex items-center gap-2 mt-1">
                  {trend.direction === 'up' ? (
                    <IoTrendingUpOutline className="w-5 h-5 text-green-500" />
                  ) : (
                    <IoTrendingDownOutline className="w-5 h-5 text-red-500" />
                  )}
                  <span className={`text-sm ${trend.direction === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                    {trend.value > 0 ? '+' : ''}{trend.value.toFixed(1)}% overall
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className={`text-2xl ${mutedTextColor} hover:${textColor} transition-colors`}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Chart */}
            {sortedHistory.length > 1 && (
              <div className={`mb-6 p-4 rounded-lg ${cardBg} border ${borderColor}`}>
                <ResponsiveContainer width="100%" height={256}>
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke={darkMode ? '#374151' : '#E5E7EB'}
                    />
                    <XAxis 
                      dataKey="date"
                      stroke={darkMode ? '#9CA3AF' : '#6B7280'}
                      tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                    />
                    <YAxis
                      stroke={darkMode ? '#9CA3AF' : '#6B7280'}
                      tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                      tickFormatter={(value) => `${value}${unit ? ` ${unit}` : ''}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${darkMode ? '#374151' : '#E5E7EB'}`,
                        borderRadius: '0.375rem'
                      }}
                      labelStyle={{ color: darkMode ? '#E5E7EB' : '#111827' }}
                      itemStyle={{ color: darkMode ? '#E5E7EB' : '#111827' }}
                    />
                    <Legend 
                      wrapperStyle={{ color: darkMode ? '#E5E7EB' : '#111827' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      name={metricName}
                      stroke={darkMode ? '#60A5FA' : '#3B82F6'}
                      strokeWidth={2}
                      dot={{ fill: darkMode ? '#60A5FA' : '#3B82F6' }}
                    />
                    {target && (
                      <ReferenceLine 
                        y={target} 
                        stroke={darkMode ? '#10B981' : '#059669'}
                        strokeDasharray="5 5"
                        label={{ value: "Target", position: "right" }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* History List */}
            <div className="space-y-3">
              <h3 className={`text-lg font-medium ${textColor} mb-3`}>
                Update History
              </h3>
              {sortedHistory.reverse().map((entry, index) => {
                const previousEntry = sortedHistory[index - 1];
                const change = previousEntry 
                  ? ((entry.metric_value - previousEntry.metric_value) / previousEntry.metric_value) * 100
                  : null;

                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 rounded-lg border ${borderColor} ${cardBg}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className={`text-2xl font-bold ${textColor}`}>
                            {entry.metric_value}
                          </span>
                          {unit && (
                            <span className={`text-lg ${mutedTextColor}`}>
                              {unit}
                            </span>
                          )}
                          {change !== null && (
                            <span className={`text-sm ${
                              change > 0 ? 'text-green-500' : change < 0 ? 'text-red-500' : 'text-gray-500'
                            }`}>
                              {change > 0 ? '+' : ''}{change.toFixed(1)}%
                            </span>
                          )}
                        </div>
                        {entry.notes && (
                          <p className={`text-sm ${mutedTextColor} mt-1`}>
                            {entry.notes}
                          </p>
                        )}
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${mutedTextColor}`}>
                        <IoTimeOutline className="w-4 h-4" />
                        <span>{new Date(entry.metric_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MetricsHistory;